export const SHEET_MARGIN_IN = 0.125   // 1/8" safety margin from sheet edge
export const ITEM_MARGIN_IN  = 0.25    // 1/4" gap between images

const GEOMETRY_EPSILON = 0.000001

function searchBeamWidth(itemCount) {
  if (itemCount > 500) return 4
  if (itemCount > 100) return 12
  return 32
}

function compareScores(left, right) {
  for (let index = 0; index < left.length; index += 1) {
    if (Math.abs(left[index] - right[index]) <= GEOMETRY_EPSILON) continue
    return left[index] - right[index]
  }
  return 0
}

function contains(outer, inner) {
  return inner.x >= outer.x - GEOMETRY_EPSILON &&
    inner.y >= outer.y - GEOMETRY_EPSILON &&
    inner.x + inner.width <= outer.x + outer.width + GEOMETRY_EPSILON &&
    inner.y + inner.height <= outer.y + outer.height + GEOMETRY_EPSILON
}

function intersects(left, right) {
  return left.x < right.x + right.width - GEOMETRY_EPSILON &&
    left.x + left.width > right.x + GEOMETRY_EPSILON &&
    left.y < right.y + right.height - GEOMETRY_EPSILON &&
    left.y + left.height > right.y + GEOMETRY_EPSILON
}

function pruneContainedRectangles(rectangles) {
  return rectangles.filter((rectangle, index) => !rectangles.some((candidate, candidateIndex) => (
    candidateIndex !== index && contains(candidate, rectangle) && (
      !contains(rectangle, candidate) || candidateIndex < index
    )
  )))
}

// MaxRects split: preserve every unused strip around the occupied rectangle,
// then remove rectangles wholly contained by a larger one.
function splitFreeRectangles(rectangles, occupied) {
  const split = []

  rectangles.forEach(rectangle => {
    if (!intersects(rectangle, occupied)) {
      split.push(rectangle)
      return
    }

    const rectangleRight = rectangle.x + rectangle.width
    const rectangleBottom = rectangle.y + rectangle.height
    const occupiedRight = occupied.x + occupied.width
    const occupiedBottom = occupied.y + occupied.height

    if (occupied.x > rectangle.x + GEOMETRY_EPSILON) {
      split.push({
        x: rectangle.x,
        y: rectangle.y,
        width: occupied.x - rectangle.x,
        height: rectangle.height,
      })
    }
    if (occupiedRight < rectangleRight - GEOMETRY_EPSILON) {
      split.push({
        x: occupiedRight,
        y: rectangle.y,
        width: rectangleRight - occupiedRight,
        height: rectangle.height,
      })
    }
    if (occupied.y > rectangle.y + GEOMETRY_EPSILON) {
      split.push({
        x: rectangle.x,
        y: rectangle.y,
        width: rectangle.width,
        height: occupied.y - rectangle.y,
      })
    }
    if (occupiedBottom < rectangleBottom - GEOMETRY_EPSILON) {
      split.push({
        x: rectangle.x,
        y: occupiedBottom,
        width: rectangle.width,
        height: rectangleBottom - occupiedBottom,
      })
    }
  })

  return pruneContainedRectangles(split.filter(rectangle => (
    rectangle.width > GEOMETRY_EPSILON && rectangle.height > GEOMETRY_EPSILON
  )))
}

function orientations(item, usableWidth, usableHeight, maximumArtworkWidth) {
  const candidates = [
    { rotation: 0, footprintWidth: item.width_in, footprintHeight: item.height_in },
  ]
  if (Math.abs(item.width_in - item.height_in) > GEOMETRY_EPSILON) {
    candidates.push({
      rotation: 90,
      footprintWidth: item.height_in,
      footprintHeight: item.width_in,
    })
  }

  return candidates.filter(candidate => (
    candidate.footprintWidth <= usableWidth + GEOMETRY_EPSILON &&
    candidate.footprintWidth <= maximumArtworkWidth + GEOMETRY_EPSILON &&
    candidate.footprintHeight <= usableHeight + GEOMETRY_EPSILON
  ))
}

function placementChoices(item, sheets, usableWidth, usableHeight, maximumArtworkWidth) {
  const candidates = orientations(item, usableWidth, usableHeight, maximumArtworkWidth)
  const choices = []

  sheets.forEach((sheet, sheetIndex) => {
    sheet.freeRectangles.forEach(rectangle => {
      candidates.forEach(candidate => {
        const packedWidth = candidate.footprintWidth + ITEM_MARGIN_IN
        const packedHeight = candidate.footprintHeight + ITEM_MARGIN_IN
        if (
          packedWidth > rectangle.width + GEOMETRY_EPSILON ||
          packedHeight > rectangle.height + GEOMETRY_EPSILON
        ) return

        choices.push({
          ...candidate,
          x: rectangle.x,
          y: rectangle.y,
          packedWidth,
          packedHeight,
          sheetIndex,
        })
      })
    })
  })

  return choices.sort((left, right) => compareScores([
    left.sheetIndex,
    left.y,
    left.x,
    left.rotation === 0 ? 0 : 1,
  ], [
    right.sheetIndex,
    right.y,
    right.x,
    right.rotation === 0 ? 0 : 1,
  ]))
}

function newSheet(horizontalMargin, usableWidth, usableHeight) {
  // The extra trailing margin means the final item need not reserve a gutter
  // against the sheet edge, while adjacent packed boxes remain ITEM_MARGIN_IN apart.
  return {
    freeRectangles: [{
      x: horizontalMargin,
      y: SHEET_MARGIN_IN,
      width: usableWidth + ITEM_MARGIN_IN,
      height: usableHeight + ITEM_MARGIN_IN,
    }],
    usedBottom: SHEET_MARGIN_IN,
  }
}

function addPlacement(state, item, choice) {
  const sheets = state.sheets.map((sheet, index) => (
    index === choice.sheetIndex
      ? {
          ...sheet,
          freeRectangles: splitFreeRectangles(sheet.freeRectangles, {
            x: choice.x,
            y: choice.y,
            width: choice.packedWidth,
            height: choice.packedHeight,
          }),
          usedBottom: Math.max(sheet.usedBottom, choice.y + choice.footprintHeight),
        }
      : sheet
  ))

  return {
    sheets,
    placements: [...state.placements, {
      ...item,
      x_in: choice.x,
      y_in: choice.y,
      rotation_deg: choice.rotation,
      sheet_number: choice.sheetIndex + 1,
    }],
    unplaced: state.unplaced,
    rotationCount: state.rotationCount + (choice.rotation === 0 ? 0 : 1),
  }
}

function stateScore(state) {
  const usedHeight = state.sheets.reduce((total, sheet) => (
    total + Math.max(0, sheet.usedBottom - SHEET_MARGIN_IN)
  ), 0)
  const freeRectangleCount = state.sheets.reduce((total, sheet) => (
    total + sheet.freeRectangles.length
  ), 0)

  return [
    state.unplaced.length,
    state.sheets.length,
    usedHeight,
    freeRectangleCount,
    state.rotationCount,
  ]
}

function stateGeometryKey(state) {
  const geometry = state.sheets.map(sheet => sheet.freeRectangles
    .map(rectangle => [rectangle.x, rectangle.y, rectangle.width, rectangle.height]
      .map(value => Number(value.toFixed(6))).join(','))
    .sort()
    .join(';'))
    .join('|')
  return `${state.unplaced.length}:${geometry}`
}

function compactStates(states, beamWidth) {
  const unique = new Map()
  states.sort((left, right) => compareScores(stateScore(left), stateScore(right)))
  states.forEach(state => {
    const key = stateGeometryKey(state)
    if (!unique.has(key)) unique.set(key, state)
  })
  return [...unique.values()].slice(0, beamWidth)
}

function unplaceableReason(item, usableWidth, usableHeight, maximumArtworkWidth) {
  const originalWidthFits = item.width_in <= usableWidth + GEOMETRY_EPSILON &&
    item.width_in <= maximumArtworkWidth + GEOMETRY_EPSILON
  const originalHeightFits = item.height_in <= usableHeight + GEOMETRY_EPSILON
  const widths = [item.width_in, item.height_in]
  const heights = [item.height_in, item.width_in]
  const widthFits = widths.some(width => (
    width <= usableWidth + GEOMETRY_EPSILON &&
    width <= maximumArtworkWidth + GEOMETRY_EPSILON
  ))
  const fullFit = widths.some((width, index) => (
    width <= usableWidth + GEOMETRY_EPSILON &&
    width <= maximumArtworkWidth + GEOMETRY_EPSILON &&
    heights[index] <= usableHeight + GEOMETRY_EPSILON
  ))

  if (fullFit) return null
  if (!originalWidthFits && originalHeightFits) return 'too_wide'
  if (originalWidthFits && !originalHeightFits) return 'too_tall'
  if (!widthFits) return 'too_wide'
  return 'too_tall'
}

/**
 * Rotation-aware MaxRects packer.
 *
 * Artwork dimensions always remain the requested physical dimensions. The
 * packer may rotate a copy by 90 degrees, but never scales it to force a fit.
 */
export function autofill(images, sheetSize, maxSheets = 10) {
  const width = Number(sheetSize.width_in)
  const height = Number(sheetSize.height_in)
  const horizontalMargin = Number.isFinite(Number(sheetSize.horizontal_safety_margin_in))
    ? Number(sheetSize.horizontal_safety_margin_in)
    : SHEET_MARGIN_IN
  const maximumArtworkWidth = Number.isFinite(Number(sheetSize.nominal_width_in))
    ? Number(sheetSize.nominal_width_in)
    : width - 2 * horizontalMargin
  const usableWidth = width - 2 * horizontalMargin
  const usableHeight = height - 2 * SHEET_MARGIN_IN
  const unplaceable = []
  const items = []

  for (const image of images) {
    const quantity = image.quantity ?? 1
    if (quantity <= 0) continue
    for (let index = 0; index < quantity; index += 1) {
      items.push({
        uploaded_image_id: image.uploaded_image_id ?? image.id,
        width_in: Number(image.width_in),
        height_in: Number(image.height_in),
        ...(image.instance_id && { instance_id: image.instance_id }),
        ...(image.flip_x != null && { flip_x: image.flip_x }),
        ...(image.flip_y != null && { flip_y: image.flip_y }),
      })
    }
  }

  items.sort((left, right) => (
    (Math.max(right.width_in, right.height_in) - Math.max(left.width_in, left.height_in)) ||
    ((right.width_in * right.height_in) - (left.width_in * left.height_in)) ||
    String(left.uploaded_image_id).localeCompare(String(right.uploaded_image_id))
  ))
  const beamWidth = searchBeamWidth(items.length)

  const placeable = []
  items.forEach(item => {
    if (
      !Number.isFinite(item.width_in) || !Number.isFinite(item.height_in) ||
      item.width_in <= 0 || item.height_in <= 0
    ) {
      unplaceable.push({ uploaded_image_id: item.uploaded_image_id, reason: 'invalid_dimensions' })
      return
    }

    const reason = unplaceableReason(item, usableWidth, usableHeight, maximumArtworkWidth)
    if (reason) unplaceable.push({ uploaded_image_id: item.uploaded_image_id, reason })
    else placeable.push(item)
  })

  let states = [{
    sheets: [],
    placements: [],
    unplaced: [],
    rotationCount: 0,
  }]

  for (const item of placeable) {
    const nextStates = []
    states.forEach(state => {
      let candidateState = state
      let choices = placementChoices(
        item, candidateState.sheets, usableWidth, usableHeight, maximumArtworkWidth
      )

      if (choices.length === 0 && candidateState.sheets.length < maxSheets) {
        candidateState = {
          ...candidateState,
          sheets: [
            ...candidateState.sheets,
            newSheet(horizontalMargin, usableWidth, usableHeight),
          ],
        }
        choices = placementChoices(
          item, candidateState.sheets, usableWidth, usableHeight, maximumArtworkWidth
        ).filter(choice => choice.sheetIndex === candidateState.sheets.length - 1)
      }

      if (choices.length === 0) {
        nextStates.push({
          ...candidateState,
          unplaced: [...candidateState.unplaced, item],
        })
      } else {
        choices.forEach(choice => nextStates.push(addPlacement(candidateState, item, choice)))
      }
    })

    states = compactStates(nextStates, beamWidth)
  }

  const best = states[0]
  best.unplaced.forEach(item => {
    unplaceable.push({ uploaded_image_id: item.uploaded_image_id, reason: 'sheet_limit' })
  })

  return { placements: best.placements, unplaceable }
}

// Packer coordinates are the rotated bounding box's top-left. Fabric and the
// persisted layout use the unrotated artwork's top-left as the rotation anchor.
export function placementAnchorCoordinates(placement) {
  const width = Number(placement.width_in)
  const height = Number(placement.height_in)
  const radians = Number(placement.rotation_deg || 0) * Math.PI / 180
  const cosine = Math.cos(radians)
  const sine = Math.sin(radians)
  const corners = [[0, 0], [width, 0], [width, height], [0, height]]
  const minimumX = Math.min(...corners.map(([x, y]) => (x * cosine) - (y * sine)))
  const minimumY = Math.min(...corners.map(([x, y]) => (x * sine) + (y * cosine)))

  return {
    x_in: placement.x_in - minimumX,
    y_in: placement.y_in - minimumY,
  }
}
